import React, { useCallback, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import {
  Box,
  Button,
  Switch,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { PortalLoadingCard, PortalStateCard } from './portalShared'

const DEFAULT_PREFS = {
  pushEnabled: true,
  smsEnabled: false,
  emailEnabled: false,
  level: 'all_updates',
  directMessages: true,
  crewMessages: true,
  vendorMessages: true,
  jobMessages: true,
  dailySummary: false,
  weeklySummary: true
}

const LEVEL_OPTIONS = [
  { value: 'urgent_only', label: 'Urgent only' },
  { value: 'messages_only', label: 'Messages only' },
  { value: 'all_updates', label: 'All updates' }
]

const CHANNEL_ITEMS = [
  { key: 'pushEnabled', label: 'Push notifications', help: 'Send device notifications when message activity matches your settings.' },
  { key: 'smsEnabled', label: 'SMS escalation', help: 'Receive text-message alerts for qualifying notifications.' },
  { key: 'emailEnabled', label: 'Email', help: 'Receive email alerts and summaries.' }
]

const MESSAGE_TYPE_ITEMS = [
  { key: 'directMessages', label: 'Direct conversations', help: '1:1 threads targeted to you.' },
  { key: 'crewMessages', label: 'Crew conversations', help: 'Messages sent to your assigned crew.' },
  { key: 'vendorMessages', label: 'Vendor-wide', help: 'Company-wide announcements and discussions.' },
  { key: 'jobMessages', label: 'Job conversations', help: 'Threads tied to a specific job or work order.' }
]

const useStyles = makeStyles((theme) => ({
  pageHead: {
    marginBottom: 20
  },
  pageTitle: {
    fontSize: 22,
    fontWeight: 700,
    letterSpacing: '-0.01em',
    color: 'var(--df-ink)',
    margin: 0
  },
  pageDesc: {
    color: 'var(--df-ink-3)',
    fontSize: 13.5,
    marginTop: 4
  },
  card: {
    background: 'var(--df-surface)',
    border: '1px solid var(--df-line)',
    borderRadius: 'var(--df-radius-lg)',
    boxShadow: 'var(--df-shadow-sm)',
    marginBottom: 16,
    overflow: 'hidden'
  },
  sectionHead: {
    padding: '16px 18px 12px',
    borderBottom: '1px solid var(--df-line)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline'
  },
  sectionLabel: {
    fontFamily: 'var(--df-font-mono)',
    fontSize: 10.5,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '.12em',
    color: 'var(--df-ink-3)',
    margin: 0
  },
  sectionMeta: {
    fontSize: 12,
    color: 'var(--df-ink-4)'
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: '14px 18px',
    borderBottom: '1px solid var(--df-line)',
    '&:last-child': {
      borderBottom: 0
    }
  },
  rowCopy: {
    flex: 1,
    minWidth: 0
  },
  rowLabel: {
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--df-ink)'
  },
  rowHelp: {
    fontSize: 12.5,
    color: 'var(--df-ink-3)',
    marginTop: 2
  },
  grid2: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    '& $row': {
      borderRight: '1px solid var(--df-line)'
    },
    '& $row:nth-child(2n)': {
      borderRight: 0
    },
    '@media (max-width: 720px)': {
      gridTemplateColumns: '1fr',
      '& $row': {
        borderRight: 0
      }
    }
  },
  segmented: {
    display: 'inline-flex',
    background: 'var(--df-bg-2)',
    border: '1px solid var(--df-line)',
    borderRadius: 8,
    padding: 2,
    gap: 2,
    flexShrink: 0
  },
  segBtn: {
    border: 0,
    background: 'transparent',
    font: 'inherit',
    fontSize: 13,
    fontWeight: 500,
    padding: '6px 12px',
    borderRadius: 6,
    color: 'var(--df-ink-2)',
    cursor: 'pointer',
    '&:hover': {
      color: 'var(--df-ink)'
    }
  },
  segBtnActive: {
    background: 'var(--df-surface)',
    color: 'var(--df-ink)',
    boxShadow: 'var(--df-shadow-sm)',
    fontWeight: 600
  },
  actions: {
    padding: '16px 18px',
    background: 'var(--df-surface-2)',
    borderTop: '1px solid var(--df-line)',
    display: 'flex',
    gap: 8,
    justifyContent: 'flex-end',
    alignItems: 'center'
  },
  savedHint: {
    fontSize: 12.5,
    color: 'var(--df-ink-3)',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    marginRight: 'auto'
  },
  savedDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: 'var(--df-ink-4)'
  },
  savedDotDirty: {
    background: 'var(--df-accent)'
  }
}))

const SubcontractorNotifications = ({ notification }) => {
  const classes = useStyles()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [prefs, setPrefs] = useState(DEFAULT_PREFS)
  const [dirty, setDirty] = useState(false)
  const [hasPhone, setHasPhone] = useState(false)
  const [phoneInput, setPhoneInput] = useState('')

  const loadPrefs = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const response = await axios.get('/api/subcontractor/v2/notification-prefs')
      setPrefs({ ...DEFAULT_PREFS, ...(response.data?.prefs || {}) })
      setHasPhone(response.data?.hasPhone === true)
      setPhoneInput(response.data?.phoneOnFile || '')
      setDirty(false)
    } catch (requestError) {
      console.error('Failed to load subcontractor notification preferences', requestError)
      setError(requestError?.response?.data?.error || 'Unable to load notification preferences right now.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadPrefs()
  }, [loadPrefs])

  const updatePref = (key, value) => {
    setPrefs((current) => ({ ...current, [key]: value }))
    setDirty(true)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const payload = { prefs }
      if (phoneInput.trim()) payload.phone = phoneInput.trim()
      const response = await axios.put('/api/subcontractor/v2/notification-prefs', payload)
      setPrefs({ ...DEFAULT_PREFS, ...(response.data?.prefs || {}) })
      setHasPhone(response.data?.hasPhone === true)
      if (response.data?.hasPhone) setPhoneInput('')
      setDirty(false)
      notification?.({
        type: 'success',
        title: 'Preferences saved',
        message: 'Notification preferences updated for your subcontractor account.'
      })
    } catch (requestError) {
      console.error('Failed to save subcontractor notification preferences', requestError)
      notification?.({
        type: 'danger',
        title: 'Save failed',
        message: requestError?.response?.data?.error || 'Unable to save notification preferences.'
      })
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <PortalLoadingCard title="Loading notifications" message="Loading your notification settings..." minHeight={260} />
  }

  if (error) {
    return <PortalStateCard title="Notifications unavailable" message={error} actionLabel="Retry" onAction={loadPrefs} tone="danger" minHeight={260} />
  }

  return (
    <Box>
      <div className={classes.pageHead}>
        <Typography variant="h5" className={classes.pageTitle}>Notifications</Typography>
        <Typography className={classes.pageDesc}>
          Choose how you want to be notified about new messages and updates.
        </Typography>
      </div>

      {/* Delivery channels */}
      <section className={classes.card}>
        <div className={classes.sectionHead}>
          <h2 className={classes.sectionLabel}>Delivery channels</h2>
          <span className={classes.sectionMeta}>Where alerts go</span>
        </div>
        {CHANNEL_ITEMS.map((item) => (
          <div className={classes.row} key={item.key}>
            <div className={classes.rowCopy}>
              <div className={classes.rowLabel}>{item.label}</div>
              <div className={classes.rowHelp}>{item.help}</div>
            </div>
            <Switch
              checked={prefs[item.key]}
              onChange={(e) => updatePref(item.key, e.target.checked)}
            />
          </div>
        ))}
        {prefs.smsEnabled && (
          <div className={classes.row} style={!hasPhone ? { background: 'var(--df-warn-bg, #fff3d9)', borderTop: '1px solid var(--df-warn-text, #d9b97a)' } : {}}>
            <div className={classes.rowCopy}>
              <div className={classes.rowLabel}>{hasPhone ? 'SMS phone number' : 'Phone number required'}</div>
              <div className={classes.rowHelp}>{hasPhone ? 'SMS alerts will be sent to this number.' : 'Enter your mobile number to receive SMS notifications.'}</div>
              <Box mt={1} display="flex" alignItems="center" style={{ gap: 8 }}>
                <TextField
                  variant="outlined"
                  size="small"
                  placeholder="(602) 555-1234"
                  value={phoneInput}
                  onChange={(e) => { setPhoneInput(e.target.value); setDirty(true) }}
                  style={{ maxWidth: 220 }}
                />
              </Box>
            </div>
          </div>
        )}
      </section>

      {/* Message types */}
      <section className={classes.card}>
        <div className={classes.sectionHead}>
          <h2 className={classes.sectionLabel}>Message types</h2>
          <span className={classes.sectionMeta}>What triggers an alert</span>
        </div>
        <div className={classes.grid2}>
          {MESSAGE_TYPE_ITEMS.map((item) => (
            <div className={classes.row} key={item.key}>
              <div className={classes.rowCopy}>
                <div className={classes.rowLabel}>{item.label}</div>
                <div className={classes.rowHelp}>{item.help}</div>
              </div>
              <Switch
                checked={prefs[item.key]}
                onChange={(e) => updatePref(item.key, e.target.checked)}
              />
            </div>
          ))}
        </div>
      </section>

      {/* Frequency & summaries */}
      <section className={classes.card}>
        <div className={classes.sectionHead}>
          <h2 className={classes.sectionLabel}>Frequency &amp; summaries</h2>
          <span className={classes.sectionMeta}>How much, how often</span>
        </div>

        <div className={classes.row}>
          <div className={classes.rowCopy}>
            <div className={classes.rowLabel}>Notification level</div>
            <div className={classes.rowHelp}>How much we send you when something happens.</div>
          </div>
          <div className={classes.segmented}>
            {LEVEL_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`${classes.segBtn} ${prefs.level === opt.value ? classes.segBtnActive : ''}`}
                onClick={() => updatePref('level', opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className={classes.row}>
          <div className={classes.rowCopy}>
            <div className={classes.rowLabel}>Daily digest</div>
            <div className={classes.rowHelp}>A morning summary of activity from the previous day.</div>
          </div>
          <Switch
            checked={prefs.dailySummary}
            onChange={(e) => updatePref('dailySummary', e.target.checked)}
          />
        </div>

        <div className={classes.row}>
          <div className={classes.rowCopy}>
            <div className={classes.rowLabel}>Weekly digest</div>
            <div className={classes.rowHelp}>A Monday roundup of last week's activity.</div>
          </div>
          <Switch
            checked={prefs.weeklySummary}
            onChange={(e) => updatePref('weeklySummary', e.target.checked)}
          />
        </div>

        <div className={classes.actions}>
          <span className={classes.savedHint}>
            <span className={`${classes.savedDot} ${dirty ? classes.savedDotDirty : ''}`} />
            <span>{dirty ? 'Unsaved changes' : 'All changes saved'}</span>
          </span>
          <Button
            variant="outlined"
            onClick={loadPrefs}
            disabled={saving}
          >
            Reload
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save preferences'}
          </Button>
        </div>
      </section>
    </Box>
  )
}

const mapStateToProps = (state) => ({
  notification: state.notification
})

export default connect(mapStateToProps)(SubcontractorNotifications)
